'use client';
import { CSSProperties, useState } from 'react';
import { useCartStore } from '@/components/shopping/useCartStore';
import RingLoader from 'react-spinners/RingLoader';
import { CartItem } from '@/lib/ecwid/ecwidTypes';

const EditItemQuantityButton = ({
    index,
    item,
}: {
    index: number;
    item: CartItem;
}) => {
    const [message, setMessage] = useState<string | null>(null);
    const [isUpdating, setIsUpdating] = useState<number | null>(null);

    const { setCart } = useCartStore(state => ({
        setCart: state.setCart,
    }));

    const handleUpdateQuantity = async (operation: 'plus' | 'minus') => {
        if (typeof window === 'undefined' || !window.Ecwid) {
            setMessage('Ecwid API is not available.');
            return;
        }

        setIsUpdating(index);
        setMessage(null);

        const newQuantity =
            operation === 'plus' ? item.quantity + 1 : item.quantity - 1;

        if (newQuantity < 1) {
            setMessage('Quantity cannot be less than 1');
            setIsUpdating(null);
            return;
        }

        try {
            if (operation === 'plus') {
                window.Ecwid.Cart.addProduct({
                    id: item.product.id,
                    quantity: 1,
                    options: item.options || {},
                    callback: function (addSuccess, addedProduct, updatedCart) {
                        if (addSuccess) {
                            setCart(updatedCart);
                        } else {
                            setMessage('Failed to update quantity.');
                        }
                        setIsUpdating(null);
                    },
                });
            } else {
                window.Ecwid.Cart.get(() => {
                    window.Ecwid.Cart.removeProduct(index, function (success) {
                        if (success) {
                            window.Ecwid.Cart.addProduct({
                                id: item.product.id,
                                quantity: newQuantity,
                                options: item.options || {},
                                callback: function (
                                    addSuccess,
                                    addedProduct,
                                    updatedCart,
                                ) {
                                    if (addSuccess) {
                                        setCart(updatedCart);
                                    } else {
                                        setMessage(
                                            'Failed to update quantity.',
                                        );
                                    }
                                    setIsUpdating(null);
                                },
                            });
                        } else {
                            setMessage('Failed to update quantity.');
                        }
                    });
                });
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error updating item quantity:', error);
            setMessage('An error occurred.');
            setIsUpdating(null);
        }
    };

    const override: CSSProperties = {
        display: 'block',
        margin: '0 auto',
        borderColor: '#000',
    };

    return (
        <div className="flex items-center gap-2">
            {isUpdating === index ? (
                <div className="flex justify-start inset-0 items-center">
                    <RingLoader
                        aria-label="Loading Spinner"
                        color="#000"
                        cssOverride={override}
                        data-testid="loader"
                        loading={isUpdating === index}
                        size={32}
                    />
                </div>
            ) : (
                <>
                    <button
                        aria-label="Decrease quantity"
                        className="pb-1 h-8 w-8 text-xl font-semibold bg-gray-700 text-white rounded"
                        disabled={isUpdating === index || item.quantity <= 1}
                        onClick={() => handleUpdateQuantity('minus')}
                    >
                        <span>-</span>
                    </button>
                    <span className="px-1 text-xl font-semibold">
                        {item.quantity}
                    </span>
                    <button
                        aria-label="Increase quantity"
                        className="pb-1 h-8 w-8 text-xl font-semibold bg-gray-700 text-white rounded"
                        disabled={isUpdating === index}
                        onClick={() => handleUpdateQuantity('plus')}
                    >
                        <span>+</span>
                    </button>
                </>
            )}
            {message && (
                <p className="text-sm text-red-500" role="alert">
                    {message}
                </p>
            )}
        </div>
    );
};

export default EditItemQuantityButton;
